import jwtDecode from "jwt-decode";
import {
  STORAGE_AUTH_USER,
  STORAGE_REFRESH_TOKEN,
  STORAGE_TOKEN,
} from "../constants";
import { CognitoRefreshToken, CognitoUser } from "amazon-cognito-identity-js";
import userPool from "./userPool";
import { checkTokenExpiration } from "./helper";
import Cookies from "js-cookie";
export interface DecodedToken {
  given_name: string;
  family_name: string;
  email: string;
  "custom:custom_business_name": string;
  phone_number: string;
  "custom:custom_businessType": string;
  locale: string;
  "custom:custom_FirstLogin": string;
  "cognito:groups": string[];
  sub: string;
  "custom:custom_userid": string;
}

/**
 * Retrieves the authentication token from localStorage.
 * @returns The auth token as a string or null if not found.
 */
export const getToken = (): string | null => {
  return localStorage.getItem(STORAGE_TOKEN);
};

/**
 * Stores the authentication token in localStorage.
 * @param token - The auth token to store.
 */
export const setToken = (token: string): void => {
  localStorage.setItem(STORAGE_TOKEN, token);
};

/**
 * Removes the authentication token from localStorage.
 */
export const removeToken = (): void => {
  localStorage.removeItem(STORAGE_TOKEN);
};
/**
 * Decodes a JWT token, extracts user information, and fetches user data.
 * This function should be used within a React component to set roles via Zustand.
 * @param token - The JWT token to decode.
 * @param setRole - Zustand store action to set the role.
 * @returns A promise that resolves to the user data.
 */
export const handleTokenAndFetchUserData = async (
  token: string,
  setRole?: (groups: string[]) => void,
): Promise<any> => {
  try {
    // Decode the token
    const decodedToken = jwtDecode<DecodedToken>(token);

    // Extract custom user ID and groups
    const userId = decodedToken["custom:custom_userid"];
    const groups = decodedToken["cognito:groups"] || [];

    // Set the user's role based on groups
    setRole?.(groups);

    // Fetch and return user data
    return { userId, groups };
  } catch (error) {
    console.error("Error handling token or fetching user data:", error);
    throw new Error("Failed to process token or fetch user data.");
  }
};
export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp: 1698262028 }>(token);
    const expirationTime = decoded.exp * 1000;
    return Date.now() > expirationTime;
  } catch (error) {
    return true;
  }
};
export const refreshToken = async (handleLogout?: () => void) => {
  try {
    const authUsername = localStorage.getItem(STORAGE_AUTH_USER);
    const authRefreshToken = localStorage.getItem(STORAGE_REFRESH_TOKEN);

    if (!authUsername || !authRefreshToken) {
      clearAuthData();
      return;
    }
    const user = new CognitoUser({
      Username: authUsername,
      Pool: userPool,
    });

    const refreshToken = new CognitoRefreshToken({
      RefreshToken: authRefreshToken,
    });

    return new Promise<string>((resolve, reject) => {
      user.refreshSession(refreshToken, async (err, session) => {
        if (err) {
          // handleAuthError(err, user, handleLogout);
          return reject(err);
        }

        const newIdToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();

        checkTokenExpiration(session);
        localStorage.setItem(STORAGE_TOKEN, newIdToken);

        resolve(newIdToken);
      });
    });
  } catch (error) {
    console.error("Unexpected error in refreshToken:", error);
  }
};

const clearAuthData = () => {
  localStorage.removeItem(STORAGE_AUTH_USER);
  localStorage.removeItem(STORAGE_REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_TOKEN);
};

const handleAuthError = (
  err: any,
  user: CognitoUser,
  handleLogout?: () => void,
) => {
  console.error("Refresh token error:", err);

  if (handleLogout) handleLogout();
  user.signOut();
  clearAuthData();
};

export const getParsedCookie = <T>(cookieName: string): T | null => {
  const cookieValue = Cookies.get(cookieName);
  try {
    return cookieValue ? (JSON.parse(cookieValue) as T) : null;
  } catch (error) {
    return null;
  }
};
